
/*
    services the application context to save/restore when user leave and come back to the application
    - save application context (any object) to the persistent storage when context properties is changed
    - load context from the persistent storage (indexed db)
    - calls handlers on data load and change
    - implements periodic context saving in background with configurable intervals
 */

import {Evented} from "@/engine/evented.js";

class appContext extends Evented {
    constructor(options) {
        super();
        this.name = options.name;
        this.updated = null;
        this.saved = null;
        this.initialized = false;
        this.contextSaveInterval = 0;
        this.getReadyTimeout = 1000;
        this.d = options.data
        this.storage = {
            get: options.storage.getContext.bind(options.storage),
            put: options.storage.storeContext.bind(options.storage),
        }
        this.updateTask = null;
        this.onValueChange = options.onValueChange; //callback calls for each changed value
        this.onChange = options.onChange;           //final call when all values has been set
        this.debug = options.debug !== undefined ? options.debug : false;
        this.onLoadSucc = options.onLoadSucc;

        this.setContextSaveInterval(options.contextSaveInterval === undefined ? 15 * 1000 : options.contextSaveInterval);

        this.storage.get(this.name).then(function (r) {
            this.initialized = true;
            if (r) {
                this.d = r;
                if (this.debug) {
                    console.log(`Context ${this.name}: the initial context data has been loaded: `, this.d);
                }
                if (options.data && typeof options.data === 'object') {
                    // set default values for properties that is
                    // missing in the loaded data
                    for (const [k, v] of Object.entries(options.data)) {
                        if (! (k in this.d)) {
                            this.d[k] = v
                        }
                    }
                }
                if (typeof this.onLoadSucc === "function")
                    this.onLoadSucc(this.d)
                if (typeof this.onValueChange === "function") {
                    for (const [k, v] of Object.entries(r)) {
                        this.onValueChange(k,v)
                    }
                }
                if (typeof this.onChange === "function") {
                    this.onChange(this.d)
                }
            } else {
                // loading was failed, use default values
                if (typeof this.onValueChange === "function") {
                    // call onValueChange for default values
                    for (const [k, v] of Object.entries(this.d)) {
                        this.onValueChange(k,v)
                    }
                }
                if (typeof this.onChange === "function") {
                    this.onChange(this.d)
                }
                if (typeof options.onLoadFailed === "function")
                    options.onLoadFailed(this.d)
            }
        }.bind(this)).catch(function (e) {
            if (typeof options.onLoadFailed === "function")
                options.onLoadFailed()
        }.bind(this))
    }

    /**
     * set writes data object to the context
     * @param data
     * @param force - force update without comparing with a stored object
     */
    set(data, force) {
        let updated = false
        for (let name in data) {
            if (this.d[name] === data[name] && ! force)
                continue

            updated = true
            let old = this.d[name]
            this.d[name] = data[name];

            this.fire(name, {from: old, to: data[name]})
            if (this.debug) {
                let c;
                try {
                    c = caller(2);
                } catch {
                    c = "unknown";
                }
                console.log(`Context ${this.name}: setting a new value ${name} = ${data[name]}, calls from ${c}`);
            }

            if (typeof this.onValueChange === "function") {
                this.onValueChange(name, data[name])
            }
        }

        if (! updated)
            return

        if (typeof this.onChange === "function") {
            this.onChange(this.d)
        }
        this.updated = new Date().getTime();
        if (this.contextSaveInterval === 0) {
            this._saveToStorage();
        }
    }

    get(name) {
        if (this.d[name] === undefined) {
            return null
        }
        return this.d[name]
    }

    setContextSaveInterval(seconds) {
        if (seconds === 0) {
            if (this.updateTask != null) {
                clearInterval(this.updateTask)
                this.updateTask = null;
            }
        } else {
            if (this.updateTask == null) {
                this.updateTask = setInterval(
                    function () {
                        if (this.updated !== this.saved) {
                            this._saveToStorage();
                        }
                    }.bind(this),
                    seconds
                );
            }
        }

        this.contextSaveInterval = seconds;
    }

    getReady(callback) {
        let timestamp = new Date().getTime()

        let timer = setInterval(function () {
            if (this.initialized) {
                clearInterval(timer)
                if (this.debug) {
                    console.log(`Context ${this.name}: ready after ${new Date().getTime() - timestamp}ms`);
                }
                callback(true)
                return
            }
            //timeout
            if (new Date().getTime() - timestamp > this.getReadyTimeout) {
                clearInterval(timer)
                if (this.debug) {
                    console.log(`Context ${this.name}: loading timeout ${this.getReadyTimeout}ms`);
                }
                callback(false)
                return
            }
        }.bind(this), 50);
    }

    _saveToStorage() {
        this.updated = this.saved
        this.storage.put(this.name, this.d)
        if (this.debug) {
            console.log(`Context ${this.name}: setting is saved to the storage`);
        }
    }

    data() {
        return Object.assign({}, this.d);
    }
}

export {appContext};
